import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import CompanyBranding from './components/CompanyBranding';
import GoogleAuthButton from './components/GoogleAuthButton';
import SecurityBadges from './components/SecurityBadges';
import SystemStatus from './components/SystemStatus';
import LanguageSelector from './components/LanguageSelector';
import SessionTimeoutWarning from './components/SessionTimeoutWarning';

const GoogleOAuthLogin = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [sessionTimeRemaining, setSessionTimeRemaining] = useState(300);

  // Check if user is already authenticated
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const expiry = localStorage.getItem('authExpiry');
    
    if (token && expiry && Date.now() < parseInt(expiry)) {
      // User is already authenticated, redirect to dashboard
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      if (userData?.role === 'admin') {
        navigate('/admin-schedule-management-dashboard');
      } else {
        navigate('/employee-shift-calendar');
      }
    }
  }, [navigate]);

  // Mock session timeout warning (in real app, this would be triggered by token expiry)
  useEffect(() => {
    const timer = setTimeout(() => {
      // Simulate session timeout warning after 2 minutes of inactivity
      // setShowSessionWarning(true);
    }, 120000);

    return () => clearTimeout(timer);
  }, []);

  const handleAuthSuccess = (userData) => {
    setError('');
    
    // Role-based redirection
    if (userData?.role === 'admin') {
      navigate('/admin-schedule-management-dashboard');
    } else {
      navigate('/employee-shift-calendar');
    }
  };

  const handleAuthError = (errorMessage) => {
    setError(errorMessage);
    setIsLoading(false);
  };

  const handleExtendSession = () => {
    setShowSessionWarning(false);
    // In real app, this would refresh the token
    localStorage.setItem('authExpiry', Date.now() + (8 * 60 * 60 * 1000));
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('authExpiry');
    setShowSessionWarning(false);
    window.location?.reload();
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Language Selector */}
        <div className="flex justify-end mb-6">
          <LanguageSelector />
        </div>

        {/* Main Login Card */}
        <div className="bg-card border border-border rounded-xl shadow-card p-8">
          {/* Company Branding */}
          <CompanyBranding />

          {/* Authentication Form */}
          <div className="space-y-6">
            {/* Error Message */}
            {error && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <Icon name="AlertCircle" size={16} className="text-error" />
                  <p className="text-sm text-error font-medium">Anmeldefehler</p>
                </div>
                <p className="text-sm text-error/80 mt-1">{error}</p>
                <div className="mt-3 text-xs text-muted-foreground">
                  <p>Bei anhaltenden Problemen wenden Sie sich an:</p>
                  <p className="font-medium">IT-Support: support@company.com</p>
                </div>
              </div>
            )}

            {/* Google Auth Button */}
            <GoogleAuthButton
              onAuthSuccess={handleAuthSuccess}
              onAuthError={handleAuthError}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
            />

            {/* Alternative Login Info */}
            <div className="text-center">
              <p className="text-xs text-muted-foreground">
                Verwenden Sie Ihr Unternehmens-Google-Konto
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Nur autorisierte Mitarbeiter haben Zugang
              </p>
            </div>
          </div>

          {/* Security Badges */}
          <div className="mt-8">
            <SecurityBadges />
          </div>
        </div>

        {/* System Status */}
        <div className="mt-6">
          <SystemStatus />
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <div className="flex items-center justify-center space-x-4 text-xs text-muted-foreground">
            <span>© {new Date()?.getFullYear()} Enterprise Solutions</span>
            <span>•</span>
            <button className="hover:text-foreground transition-colors">
              Datenschutz
            </button>
            <span>•</span>
            <button className="hover:text-foreground transition-colors">
              Impressum
            </button>
          </div>
          <div className="mt-2 flex items-center justify-center space-x-2 text-xs text-muted-foreground">
            <Icon name="Globe" size={12} />
            <span>Version 2.1.0 - Letzte Aktualisierung: 03.10.2025</span>
          </div>
        </div>

        {/* Mock Credentials Info */}
        <div className="mt-6 bg-muted/50 border border-border rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Info" size={16} className="text-primary" />
            <h3 className="text-sm font-medium text-foreground">Demo-Anmeldedaten</h3>
          </div>
          <div className="text-xs text-muted-foreground space-y-1">
            <p><strong>Mitarbeiter:</strong> john.doe@company.com</p>
            <p><strong>Administrator:</strong> admin@company.com</p>
            <p className="mt-2 text-warning">Nur für Demonstrationszwecke</p>
          </div>
        </div>
      </div>
      {/* Session Timeout Warning Modal */}
      <SessionTimeoutWarning
        isVisible={showSessionWarning}
        onExtendSession={handleExtendSession}
        onLogout={handleLogout}
        timeRemaining={sessionTimeRemaining}
      />
    </div>
  );
};

export default GoogleOAuthLogin;